import {
    Card,
    Loading,
    BaseTable,
    TableHeader,
} from "components"
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  TimeScale
} from "chart.js";
import "chartjs-adapter-date-fns";
import ChartDataLabels from "chartjs-plugin-datalabels";

import { useProfileDetail } from "../utils/useProfileDetail"
import moment from "moment"
import { useEffect, useState } from "react"
import { FormStateProjectProfile } from "../types/ProfileTypes"
import { convertNumber } from "utils"
import {useDashboardList } from "../utils/useDashboardList"
import { BudgetListResponse, DataDashboardResponse, MilstoneListResponse, PaymentListResponse } from "../types/DashboardType";

type TabDashboardProps = {
  action?:string
  project_id?:string
  approval_view?:boolean
  data_profile?:FormStateProjectProfile
}

ChartJS.register(
    LineElement,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Tooltip,
    Legend,
    TimeScale,
    ChartDataLabels
);

export const TabDashboard = (props: TabDashboardProps) => {
  let accumulationBudget = 0;
  let accumulationMilestone = 0;
  let accumulationPayment = 0;

  const { data } = useProfileDetail(props?.project_id)
  const { data: dataDashboard, isLoading: loadingDashboard } = useDashboardList(Number(props.project_id))

  const [isLoading , setLoading] = useState(true)
  const [dataProfile, setDataProfile] = useState<FormStateProjectProfile | null>(null);
  const [dayBehind,setDayBehind ] = useState(0)
  const [weekBehind,setWeekBehind ] = useState(0)

  useEffect(() => {
    if (isLoading && ((!data || data?.length === 0))) return;

    setDataProfile(data[0])
    const startDate = data[0]?.start_date;
    const dueDate = data[0]?.due_date;
    const today = moment().toDate()
    
    const dayBehind = startDate ? moment(dueDate).diff(moment(today), "days") : 0;
    let weekBehind =  startDate ? moment(today).diff(moment(startDate), "days") : 0;
    weekBehind = Math.ceil(weekBehind / 7 );
    
    setDayBehind(dayBehind)
    setWeekBehind(weekBehind)
    setLoading(false)
    // eslint-disable-next-line
  },[data])

  return(
    <div>
      <Card>
        <Card.Body>
          <div className="flex flex-row w-full"> 
            <div className="grow">
              <div className="text-2xl font-bold uppercase py-4">{dataProfile?.project_name}</div>
              <table>
                <tbody>
                  <tr>
                    <td>START DATE</td>
                    <td>:</td>
                    <td>{(dataProfile?.start_date) ? moment(dataProfile.start_date).format('DD/MM/YYYY') : '-'}</td>
                  </tr>
                  <tr>
                    <td>DUE DATE</td>
                    <td>:</td>
                    <td>{(dataProfile?.due_date) ? moment(dataProfile.due_date).format('DD/MM/YYYY') : '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="flex gap-2">
              <div className="min-h-[80px] flex flex-col border border-black">
                <div className="min-h-[40px] bg-black font-bold text-white text-sm text-center py-2 px-2">DURATION | DAYS</div>

                <section className="grow font-bold flex flex-col justify-center gap-1 p-3 text-center">
                  <span className="text-lg">{dayBehind}</span>
                  <span className="text-sm">Leaf of days</span>
                </section>
              </div>
              <div className="min-h-[80px] flex flex-col border border-black">
                <div className="min-h-[40px] bg-black font-bold text-white text-sm text-center py-2 px-2">START | WEEKS</div>

                <section className="grow font-bold flex flex-col justify-center gap-1 p-3 text-center">
                  <span className="text-lg">{weekBehind}</span>
                  <span className="text-sm">Weeks</span>
                </section>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <div>
        <div className="w-full p-4 max-h-[600px)]">
          <div className="w-full whitespace-nowrap font-bold mb-2">CHART DATA</div>
          {loadingDashboard ? <Loading loading={loadingDashboard} errorText="No data available" /> : <ChartDasboard dataDashboard={dataDashboard}/>}  
        </div>

        <div className="flex lg:grid-cols-2 gap-[50px] justify-between p-3 overflow-x-auto">
          <div className="w-full">
            <div className="w-40 whitespace-nowrap font-bold mb-2 text-[#8faadb]">BUDGET</div>
            <div className="overflow-x-auto max-h-[calc(100vh-500px)]">
              <BaseTable>
                <TableHeader backgroundColor="!bg-[#8faadb]">
                  <th>NO</th>
                  <th>DATE</th>
                  <th>BOQ</th>
                  <th>RESOURCE</th>
                  <th>BUDGET</th>
                  <th>ACCUMULATION</th>
                </TableHeader>
                <tbody>
                  {dataDashboard?.budget?.map((item, index) => {
                    const budget = Number(item?.budget ?? 0);
                    accumulationBudget += budget;

                    return(
                      <tr key={index} >
                        <td className="text-center">{index+1}</td>
                        <td>{item?.due_date ? moment(item?.due_date).format("DD/MM/YYYY") : "-"}</td>
                        <td>{item?.boq_name ?? "-"}</td>
                        <td>{item?.vendor_name ?? "-"}</td>
                        <td className="text-right">{item?.budget ? convertNumber(item?.budget).intoCurrency : "-"}</td>
                        <td className="text-right">{accumulationBudget ? convertNumber(Number(accumulationBudget)).intoCurrency : "-"}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </BaseTable>
            </div>
          </div>

        <div className="w-full">
            <div className="w-40 whitespace-nowrap font-bold mb-2 text-[#06b050]">MILESTONE</div>
            <div className="overflow-x-auto max-h-[calc(100vh-500px)]">
              <BaseTable>
                <TableHeader backgroundColor="!bg-[#06b050]">
                  <th>NO</th>
                  <th>DATE</th>
                  <th>BOQ</th>
                  <th>RESOURCE</th>
                  <th>MILESTONE</th>
                  <th>ACCUMULATION</th>
                </TableHeader>
                <tbody>
                  {dataDashboard?.milestone?.map((item, index) => {
                    const budget = Number(item?.actual_amount ?? 0);
                    accumulationMilestone += budget;

                    return(
                      <tr key={index} >
                        <td className="text-center">{index+1}</td>
                        <td>{item?.trx_date ? moment(item?.trx_date).format("DD/MM/YYYY") : "-"}</td>
                        <td>{item?.boq_name ?? "-"}</td>
                        <td>{item?.vendor_name ?? "-"}</td>
                        <td className="text-right">{item?.actual_amount ? convertNumber(item?.actual_amount).intoCurrency : "-"}</td>
                        <td className="text-right">{accumulationMilestone ? convertNumber(Number(accumulationMilestone)).intoCurrency : "-"}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </BaseTable>
            </div>
          </div>

          <div className="w-full">
            <div className="w-40 whitespace-nowrap font-bold mb-2 text-[#ff0000]">PAYMENT</div>
            <div className="overflow-x-auto max-h-[calc(100vh-500px)]">
              <BaseTable>
                <TableHeader backgroundColor="!bg-[#ff0000]">
                  <th>NO</th>
                  <th>DATE</th>
                  <th>BOQ</th>
                  <th>RESOURCE</th>
                  <th>PAYMENT</th>
                  <th>ACCUMULATION</th>
                </TableHeader>
                <tbody>
                  {dataDashboard?.payment?.map((item, index) => {
                    const budget = Number(item?.payment_amount ?? 0);
                    accumulationPayment += budget;

                    return(
                      <tr key={index} >
                        <td className="text-center">{index+1}</td>
                        <td>{item?.trx_date ? moment(item?.trx_date).format("DD/MM/YYYY") : "-"}</td>
                        <td>{item?.boq_name ?? "-"}</td>
                        <td>{item?.vendor_name ?? "-"}</td>
                        <td className="text-right">{item?.payment_amount ? convertNumber(item?.payment_amount).intoCurrency : "-"}</td>
                        <td className="text-right">{accumulationPayment ? convertNumber(Number(accumulationPayment)).intoCurrency : "-"}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </BaseTable>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** --------------------- Chats --------------------- */
type ChartDasboardProps = {
  dataDashboard: DataDashboardResponse
}
type CombinedItem = {
  date: string;
  budget?: number | null;
  payment?: number | null;
  milestone?: number | null;
};

export const ChartDasboard = (props: ChartDasboardProps) => {
  const [chartType, setChartType] = useState<"line" | "bar">("line")
  
  const hasData = props?.dataDashboard?.budget?.length > 0 || props?.dataDashboard?.payment?.length > 0 || props?.dataDashboard?.milestone?.length > 0;

  /** Jika tidak ada data maka hanya tampilkan chart kosong */
  if (!hasData) {
    const emptyData = {
      labels: [],
      datasets: [
        { label: "BUDGET", data: [], borderColor: "#8faadb" },
        { label: "MILESTONE", data: [], borderColor: "#06b050" },
        { label: "PAYMENT", data: [], borderColor: "#ff0000" },
      ],
    };

    const emptyOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { title: { display: true, text: "Date" } },
        y: { beginAtZero: true, title: { display: true, text: "Amount" } },
      },
    };

    return (
      <div className="w-full">
        <div className="flex justify-end mb-2">
          <ToggleChartType chartType={chartType} setChartType={setChartType} />
        </div>
        <div className="w-full h-[400px]">
          {chartType === "line" ? <Line data={emptyData} options={emptyOptions} /> : <Bar data={emptyData} options={emptyOptions} />}
        </div>
      </div>
    );
  }

  const maxBudget: BudgetListResponse[] = Object.values(
    props?.dataDashboard?.budget.reduce((acc, item) => {
      if (!acc[item?.due_date] || item?.budget > acc[item?.due_date]?.budget) {
        acc[item?.due_date] = { ...item };
      }
      return acc;
    }, {} as Record<string, BudgetListResponse>)
  );
  const maxMilestone: MilstoneListResponse[] = Object.values(
    props?.dataDashboard?.milestone?.reduce((acc, item) => {
      if (!acc[item?.trx_date] || item?.actual_amount > acc[item?.trx_date]?.actual_amount) {
        acc[item?.trx_date] = { ...item };
      }
      return acc;
    }, {} as Record<string, MilstoneListResponse>)
  );
  const maxPayment: PaymentListResponse[] = Object.values(
    props?.dataDashboard?.payment?.reduce((acc, item) => {
      if (!acc[item?.trx_date] || item?.payment_amount > acc[item?.trx_date]?.payment_amount) {
        acc[item?.trx_date] = { ...item };
      }
      return acc;
    }, {} as Record<string, PaymentListResponse>)
  );

  const sumBudgetByDate = groupAndSumByDate(props.dataDashboard.budget, "due_date", "budget");
  const sumPaymentByDate = groupAndSumByDate(props.dataDashboard.payment, "trx_date", "payment_amount");
  const sumMilestoneByDate = groupAndSumByDate(props.dataDashboard.milestone, "trx_date", "actual_amount");

  const sortByDate = (arr: any[], key: string) => {
    return arr?.sort((a, b) => new Date(a[key]).getTime() - new Date(b[key]).getTime());
  };

  const budgetSorted = sortByDate(sumBudgetByDate, "due_date");
  const paymentSorted = sortByDate(sumPaymentByDate, "trx_date");
  const milestoneSorted = sortByDate(sumMilestoneByDate, "trx_date");

  const budgetData = buildCumulative(budgetSorted, "budget", "due_date");
  const paymentData = buildCumulative(paymentSorted, "payment_amount", "trx_date");
  const milestoneData = buildCumulative(milestoneSorted, "actual_amount", "trx_date");

  const combined: CombinedItem[] = [
    ...maxBudget?.map(budget => ({ date: budget?.due_date, budget: budget?.budget })),
    ...maxMilestone?.map(milestone => ({ date: milestone?.trx_date, milestone: milestone?.actual_amount })),
    ...maxPayment?.map(payment => ({ date: payment?.trx_date, payment: payment?.payment_amount })),
  ];
  const combinedSorted = combined?.sort((a, b) => new Date(a?.date)?.getTime() - new Date(b?.date)?.getTime());
  /** Menentukan start date dan end date */
  const start = new Date(combinedSorted[0].date);
  const end = new Date(combinedSorted[combinedSorted?.length - 1].date);
  const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

  /** Menentukan unit time untuk axis */
  let unit: "day" | "week" | "month" | "year" = "day";
  if (diffDays > 800) unit = "year";
  else if (diffDays > 90) unit = "month";
  else if (diffDays > 30) unit = "week";

  /** Menentukan min date (start date - 1 hari) */
  const minX = new Date(start);
  if(unit === "week"){
    minX.setDate(minX.getDate() - 7);
  } else if(unit === "month" || unit === "year") {
    minX.setDate(minX.getDate() - 31);
  } else {
    minX.setDate(minX.getDate() - 1);
  }
    
  /** Menentukan max date (end date + 1 hari) */
  const maxX = new Date(end);
  maxX.setDate(maxX.getDate() + 1);
  const zeroStartPoint = { x: minX.toISOString(), y: 0 };
    
  const dataChart = {
    datasets: [
      {
        label: "BUDGET",
        data: [zeroStartPoint, ...budgetData],
        borderColor: "#8faadb",
        borderWidth: 3,
        tension: 0,
        spanGaps: true,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: "MILESTONE",
        data: [zeroStartPoint, ...milestoneData],
        borderColor: "#06b050",
        borderWidth: 3,
        tension: 0,
        spanGaps: true,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: "PAYMENT",
        data: [zeroStartPoint, ...paymentData],
        borderColor: "#ff0000",
        borderWidth: 3,
        tension: 0,
        spanGaps: true,
        pointRadius: 3,
        pointHoverRadius: 5,
      }
    ]
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const },
      datalabels: { display:false },
      tooltip: {
        mode: "nearest" as const,
        intersect: true,
        callbacks: {
          title: (items: any[]) => {
            const rawX = items[0]?.raw?.x;
            return rawX ? moment(rawX).format("DD/MM/YYYY") : "";
          },
          label: (context: any) => {
            const value = context.raw?.y;
            return `${convertNumber(value).intoCurrency}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: "time" as const, 
        time: {
          unit,
          min: minX,
          max: maxX,
          displayFormats: {
            day: "dd/MM/yyyy",
            week: "dd/MM/yyyy",
            month: "MM/yyyy",
            year: "yyyy"
          },
        },
        title: { display: true, text: "Date" },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value: any) {
            return convertNumber(value).intoCurrency;
          },
        },
        title: { display: true, text: "Amount" },
      },
    },
  };

  return (
    <div className="w-full">
      <div className="flex justify-end mb-2">
        <ToggleChartType chartType={chartType} setChartType={setChartType} />
      </div>
      <div className="w-full h-[500px]">
        {chartType === "line"
          ? <Line className="w-full" data={dataChart} options={options} />
          : <Bar className="w-full" data={dataChart} options={options} />
        }
      </div>
    </div>
  )
}

/** --------------------- Toggle Chart --------------------- */
type ToggleChartProps = {
  chartType: "line" | "bar"
  setChartType: (type: "line" | "bar") => void
}
const ToggleChartType = (props: ToggleChartProps) => {
  return (
    <select
      className="select select-bordered select-sm w-[120px]"
      value={props.chartType}
      onChange={(e) => props.setChartType(e.target.value as "line" | "bar")}
    >
      <option value="line">Line</option>
      <option value="bar">Bar</option>
    </select>
  )
}

/** --------------------- Utilities --------------------- */
const buildCumulative = (arr: any[], valueKey: string, dateKey: string) => {
  let total = 0;

  return arr.map(item => {
    const value = Number(item[valueKey] ?? 0);
    total += value;

    return {
      x: item[dateKey],
      y: total
    };
  });
};
const groupAndSumByDate = <T,>(data: T[], dateKey: keyof T, valueKey: keyof T) => {
  return Object.values(
    data.reduce((acc: any, item: any) => {
      const date = item[dateKey];
      const value = Number(item[valueKey] ?? 0);

      if (!acc[date]) {
        acc[date] = { ...item, [valueKey]: value };
      } else {
        acc[date][valueKey] += value;
      }

      return acc;
    }, {})
  );
};